import React, { forwardRef, useRef, useState } from "react";

import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/models/dom";
import "tinymce/skins/ui/oxide/skin.min.css";

import { Editor } from "@tinymce/tinymce-react";
import { Editor as TinyMCEEditor } from "tinymce";
import { Button } from "react-bootstrap";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { editorImgUpload } from "../lib/api";
import { CONTENT_IMAGE_URL } from "../lib/globals";

interface TinyEditorProps<T extends FieldValues> {
    showSave?: boolean;
    formField?: Path<T>;
    editorValue: string;
    setEditorValue?: React.Dispatch<React.SetStateAction<string>>;
    formMethods?: UseFormReturn<T>;
}

const TinyEditor = <T extends FieldValues>(
    {
        showSave = false,
        formField,
        editorValue,
        setEditorValue,
        formMethods: methods,
    }: TinyEditorProps<T>,
    ref: React.Ref<TinyMCEEditor | null>,
): React.ReactElement => {
    const editorRef = useRef<TinyMCEEditor | null>(null);
    const [dirty, setDirty] = useState(false);

    const save = () => {
        if (editorRef.current) {
            const content = editorRef.current.getContent();
            setDirty(false);
            editorRef.current.setDirty(false);
            console.log(content);
        }
    };

    const handleImageUpload = (blobInfo: any): Promise<string> => {
        return editorImgUpload(blobInfo.blob());
    };

    return (
        <>
            <Editor
                tinymceScriptSrc={"/tinymce/tinymce.min.js"}
                licenseKey="gpl"
                onInit={(_evt, editor) => (editorRef.current = editor)}
                onDirty={() => setDirty(true)}
                value={editorValue}
                onEditorChange={(newValue, editor) => {
                    setEditorValue && setEditorValue(newValue);
                    if (methods && formField) {
                        methods.setValue(
                            formField,
                            newValue as PathValue<T, any>,
                            {
                                shouldDirty: true,
                            },
                        );
                    }
                }}
                init={{
                    branding: false,
                    height: 500,
                    menubar: false,
                    plugins: ["link", "image", "table", "lists"],
                    toolbar:
                        "undo redo | blocks | bold italic underline | alignleft aligncenter alignright | indent outdent | bullist numlist | table image link",
                    object_resizing: true,
                    automatic_uploads: true,
                    images_reuse_filename: true,
                    image_description: false,
                    image_prepend_url: CONTENT_IMAGE_URL,
                    images_upload_handler: handleImageUpload,
                    contextmenu: false,
                }}
            />
            {showSave && (
                <>
                    <Button onClick={save} disabled={!dirty}>
                        Save
                    </Button>
                    {dirty && <p>You have unsaved content!</p>}
                </>
            )}
        </>
    );
};

export default forwardRef(TinyEditor) as <T extends FieldValues>(
    props: TinyEditorProps<T> & { ref?: React.Ref<TinyMCEEditor | null> },
) => React.ReactElement;
